import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { ListboxModule } from 'primeng/listbox';
import { MessageModule } from 'primeng/message';
import { finalize, Subject, Subscription, take } from 'rxjs';
import { collapseAnimation } from 'src/app/shared/animations/collapse.animation';
import { ISelectButtonColoredItem, SelectButtonColoredComponent } from 'src/app/shared/components/select-button-colored/select-button-colored.component';
import { CURRENCIES, Currency } from 'src/app/shared/enums/price/currencies.enum';
import { SessionLecturerState } from 'src/app/shared/enums/session-group/session-lecturer-states.enum';
import { SessionLecturerType } from 'src/app/shared/enums/session-group/session-lecturer-types.enum';
import { Color } from 'src/app/shared/enums/utils/colors';
import { IPrice } from 'src/app/shared/models/price/price.model';
import { ISessionGroup } from 'src/app/shared/models/session-group/session-group.model';
import { ISessionLecturerAttendancePostData } from 'src/app/shared/models/session-group/session-lecturer-attendance-post-data.model';
import { ILecturer } from 'src/app/shared/models/user/lecturer/lecturer.model';
import { LocalizedDatePipe } from 'src/app/shared/pipes/localized-date.pipe';
import { UserNameOrEmailPipe } from 'src/app/shared/pipes/user-name-or-email.pipe';
import { ISessionLecturerAttendanceSession, ISessionLecturerAttendanceSessionGroup, SessionGroupsService } from 'src/app/shared/services/entities/session-groups/session-groups.service';
import { ICallablesSessionsGetForSessionLecturerAttendanceInputGroupResult, SessionsService } from 'src/app/shared/services/entities/sessions/sessions.service';
import { ShopItemHelperService } from 'src/app/shared/services/helpers/shop-item-helper.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { SelectedOrgService } from 'src/app/shared/services/selected-org.service';
import { ILecturersState, LecturersStoreService } from 'src/app/shared/services/store/lecturers-store.service';
import { SessionsLecturerStoreService } from 'src/app/shared/services/store/sessions-lecturer-store.service';
import { UserStoreService } from 'src/app/shared/services/store/user-store.service';
import { UtilsService } from 'src/app/shared/services/utils.service';
import { getCurrencySymbol } from '@angular/common';
import { ConfirmModalComponent } from 'src/app/shared/modals/confirm-modal/confirm-modal.component';
import { getHourRateFG, HourRateFgComponent } from 'src/app/shared/components/hour-rate-fg/hour-rate-fg.component';
import { CurrencyService } from 'src/app/shared/services/currency.service';
import { AutoFocusModule } from 'primeng/autofocus';

export interface ISessionLecturerAttendanceInputGroupForm {
  regularFG?: FormGroup<ISessionLecturerAttendanceRegularForm>;
  substitutionFG?: FormGroup<ISessionLecturerAttendanceSubstitutionForm>;
  detailsFG: FormGroup<ISessionLecturerAttendanceDetailsForm>;
}

interface ISessionLecturerAttendanceRegularForm {
  lecturingWith: FormControl<number[]>;
  state: FormControl<SessionLecturerState | null>;
};

interface ISessionLecturerAttendanceSubstitutionForm {
  sessionGroup: FormControl<ISessionGroup | null>;
  session: FormControl<ISessionLecturerAttendanceSession | null>;
  lecturingWith: FormControl<number[]>;
};

interface ISessionLecturerAttendanceDetailsForm {
  note: FormControl<string | null>;
  otherExpenses: FormGroup<IOtherExpensesFG>;
};

interface IOtherExpensesFG {
  value: FormControl<number | null>;
  currency: FormControl<Currency>;
};

@Component({
  selector: 'app-session-lecturer-attendance-input-group',
  standalone: true,
  imports: [
    ReactiveFormsModule, TranslateModule, NgTemplateOutlet, AsyncPipe,
    ButtonModule, MessageModule, ListboxModule, InputTextareaModule, InputTextModule, InputNumberModule, DropdownModule, AutoFocusModule,
    SelectButtonColoredComponent, LocalizedDatePipe, HourRateFgComponent
  ],
  templateUrl: './session-lecturer-attendance-input-group.component.html',
  styleUrl: './session-lecturer-attendance-input-group.component.scss',
  animations: [
    collapseAnimation
  ]
})
export class SessionLecturerAttendanceInputGroupComponent implements OnInit, OnDestroy {
  SessionLecturerState = SessionLecturerState;
  SessionLecturerType = SessionLecturerType;
  CURRENCIES = CURRENCIES;
  getCurrencySymbol = getCurrencySymbol;

  @Input({required: true}) sessionId: number | null = null;
  @Input({required: true}) type: SessionLecturerType | undefined;


  lecturersState: ILecturersState | undefined;

  lecturerSelectItems: SelectItem[] = [];

  FG = new FormGroup<ISessionLecturerAttendanceInputGroupForm>({
    regularFG: new FormGroup<ISessionLecturerAttendanceRegularForm>({
      lecturingWith: new FormControl([], { nonNullable: true }),
      state: new FormControl(null, { validators: [  ]})
    }),
    substitutionFG: new FormGroup<ISessionLecturerAttendanceSubstitutionForm>({
      sessionGroup: new FormControl(null, { validators: [ Validators.required ] }),
      session: new FormControl(null, { validators: [ Validators.required ] }),
      lecturingWith: new FormControl([], { nonNullable: true }),
    }),
    detailsFG: new FormGroup<ISessionLecturerAttendanceDetailsForm>({
      note: new FormControl(null),
      otherExpenses: new FormGroup<IOtherExpensesFG>({
        value: new FormControl(null),
        currency: new FormControl(Currency.CZK, { nonNullable: true })
      }),
    })
  });
  otherExpensesHelperFG = getHourRateFG();


  data: ICallablesSessionsGetForSessionLecturerAttendanceInputGroupResult | null = null;
  sessionGroups: ISessionLecturerAttendanceSessionGroup[] = [];
  notClosedSessions: ISessionLecturerAttendanceSession[] = [];
  lecturers: ILecturer[] = [];

  sending: boolean = false;
  @HostBinding('class.loading-box') fetchingSession: boolean = false;
  fetchingSessionGroups: boolean = false;
  @ViewChild('otherExpensesPriceTemplate') otherExpensesPriceTemplate: TemplateRef<any> | undefined;

  subs: Subscription[] = [];

  options: ISelectButtonColoredItem[] = [];

  constructor(
    private utilsService: UtilsService,
    private userStore: UserStoreService,
    private userNameOrEmailPipe: UserNameOrEmailPipe,
    private sessionsLecturerStore: SessionsLecturerStoreService,
    private sessionGroupsService: SessionGroupsService,
    private lecturersStore: LecturersStoreService,
    private modalService: ModalService,
    public selectedOrg: SelectedOrgService,
    public siHelper: ShopItemHelperService,
    private translate: TranslateService,
    private sessionsService: SessionsService,
    private currencyService: CurrencyService,
  ) {
  }

  async ngOnInit() {

    // fetch session - its important to await for it before anything else
    if (this.type === SessionLecturerType.REGULAR) {
      await this.fetchData();
    }
    
    this.fetchLecturers();

    if (this.type === SessionLecturerType.SUBSTITUTION) {
      this.fetchSessionGroups();
      this.watchSessionGroupFC();
      this.watchSessionFC();
    }

    if (this.data?.userSessionLecturer) {
      this.FG.controls.regularFG?.patchValue({
        ...this.data.userSessionLecturer, lecturingWith: <any>this.lecturers.filter(x => this.data!.userSessionLecturer!.lecturingWith.includes(x.id)),
      }, { emitEvent: false });
      this.FG.controls.detailsFG?.patchValue({
        note: this.data.userSessionLecturer.notes.at(-1)?.text ?? null, otherExpenses: { value: this.data.userSessionLecturer.otherExpenses?.value ?? null, currency: this.data.userSessionLecturer.otherExpenses?.currency || this.currencyService.getCurrentCurrency() }
      }, { emitEvent: false });
    
      if (this.data.userSessionLecturer.hourRate?.currency) {
        this.FG.controls.detailsFG.controls.otherExpenses.controls.currency.setValue(this.data.userSessionLecturer.hourRate.currency, { emitEvent: false });
        this.FG.controls.detailsFG.controls.otherExpenses.controls.currency.disable({ emitEvent: false }); // disable currency select so the currency of this and hourRate is the same
      }
    }


    if (this.data?.session?.lecturerAttendanceClosed) {
      this.FG.disable({ emitEvent: false });
    }

    this.createOptions();
    this.subs.push(this.translate.onLangChange.subscribe(() => {
      this.createOptions();
      this.createLecturerSelectItems();
    }));
  }

  private createOptions() {
    this.options = [
      { label: this.translate.instant('admin.session-lecturer-attendance-modal.not-lecturing'), value: SessionLecturerState.ABSENT, color: Color.DANGER, icon: 'pi pi-times' },
      { label: this.translate.instant('admin.session-lecturer-attendance-modal.lecturing'), value: SessionLecturerState.PRESENT, color: Color.SUCCESS, icon: 'pi pi-check' }
    ];
  }

  onSubmit() {
    this.utilsService.markFormGroupDirty(this.FG);
    // console.log('submit', this.FG);
    if (this.sending || !this.data?.session || !this.type) {
      return;
    }

    let data: ISessionLecturerAttendancePostData | null = null;
    const detailsFormValues = this.FG.controls.detailsFG.getRawValue();

    const resultSubject = new Subject<boolean>();

    if (this.type === SessionLecturerType.REGULAR) {
      if (!this.FG.controls.regularFG || this.FG.controls.regularFG.invalid) {
        return;
      }
      const formValue = this.FG.controls.regularFG.getRawValue();
      data = {
        sessionId: this.data.session.id,
        state: formValue.state!,
        lecturingWith: formValue.state === SessionLecturerState.PRESENT ? formValue.lecturingWith : [],
        type: this.type,

        note: detailsFormValues.note,
        otherExpenses: detailsFormValues.otherExpenses,
      };
    }
    if (this.type === SessionLecturerType.SUBSTITUTION) {
      if (!this.FG.controls.substitutionFG || this.FG.controls.substitutionFG.invalid) {
        return;
      }
      const formValue = this.FG.controls.substitutionFG.getRawValue();
      data = {
        sessionId: this.data.session.id,
        state: SessionLecturerState.PRESENT,
        lecturingWith: formValue.lecturingWith,
        type: this.type,

        note: detailsFormValues.note,
        otherExpenses: detailsFormValues.otherExpenses,
      };
    }
    if (!data) {
      this.utilsService.logError(`SessionLecturer attendance no data! ${this.type}, o12i3joijy98t82wewr`);
      return;
    }

    this.sending = true;
    this.sessionsLecturerStore.postSessionLecturerAttendance(data).pipe(
      finalize(() => this.sending = false)
    ).subscribe({
      next: (res) => {
        resultSubject.next(true);
      }
    });

    return resultSubject;
  }

  private watchSessionGroupFC() {
    this.subs.push(
      this.FG.controls.substitutionFG?.controls.sessionGroup?.valueChanges.subscribe(val => {
        this.notClosedSessions = this.sessionGroups.find(x => x.id === val?.id)?.notClosedSessions ?? [];
        this.FG.controls.substitutionFG?.controls.session?.setValue(null);
      }) ?? Subscription.EMPTY
    );
  }
  private watchSessionFC() {
    this.subs.push(
      this.FG.controls.substitutionFG?.controls.session?.valueChanges.subscribe(async val => {
        this.sessionId = val?.id ?? null;
        await this.fetchData();
        this.lecturingWithFC?.reset();
        this.createLecturerSelectItems();
      }) ?? Subscription.EMPTY
    );
  }

  private async fetchData() {
    return new Promise((resolve, reject) => {
      if (!this.sessionId) {
        return reject('Session ID is not set');
      }
      this.fetchingSession = true;
      this.sessionsService.getForSessionLecturerAttendanceInputGroup({
        sessionId: this.sessionId,
      }).pipe(
        take(1)
      ).subscribe({
        next: (res) => {
          this.data = res;
          this.fetchingSession = false;
          resolve(res);
        },
        error: (err) => {
          this.fetchingSession = false;
          reject(err);
        }
      })

    })
  }

  private fetchSessionGroups() {
    // fetch sessionGroups of organizations where user is lecturer
    this.fetchingSessionGroups = true;
    this.sessionGroupsService.getForSessLectAttendance().pipe(
      take(1),
      finalize(() => this.fetchingSessionGroups = false)
    ).subscribe({
      next: (res) => {
        this.sessionGroups = res;
      }
    });
  }

  private fetchLecturers() {
    this.lecturersState = this.lecturersStore.state;
    this.lecturersStore.init();

    this.lecturersState.lecturers$.subscribe(lecturers => {
      this.lecturers = lecturers;
      this.createLecturerSelectItems();
    });
  }

  private createLecturerSelectItems() {
    if (!this.data) return;

    const userId = this.userStore.state.user$.getValue()?.id;
    // filter out this user
    const sessionLecturers = this.data.session.sessionLecturers?.filter(x => x.lecturer?.userId !== userId) ?? [];
    const lecturers = this.lecturers.filter(x => x.userId !== userId)

    const sessionLecturerIds = sessionLecturers.flatMap(x => x.lecturer?.id ?? []) ?? [];

    const defaultLecturersInSession = lecturers.filter(x => sessionLecturerIds.includes(x.id));
    const otherLecturersInOrg = lecturers.filter(x => !sessionLecturerIds.includes(x.id));

    // preselect default only if not state set
    if (this.FG.controls.regularFG?.controls.state.value === null) {
      this.lecturingWithFC?.setValue(defaultLecturersInSession.map(x => x.id), { emitEvent: false });
    } else {
      this.lecturingWithFC?.setValue(this.data.userSessionLecturer!.lecturingWith, { emitEvent: false });
    }

    this.lecturerSelectItems = [
      ...defaultLecturersInSession.map(x => {
        return this.createSelectItems(x, true)
      }),
      ...otherLecturersInOrg.map(x => {
        return this.createSelectItems(x)
      }),
      // {
      //   label: 'Výchozí lektoři lekce',
      //   items: defaultLecturersInSession.map(x => {
      //     return this.createSelectItems(x)
      //   })
      // },
      // {
      //   label: 'Další lektoři v organizaci',
      //   items: otherLecturersInOrg.map(x => {
      //     return this.createSelectItems(x)
      //   })
      // }
    ]
  }

  onAddOtherExpenses() {
    if (!this.data) return;
    if (this.data.session.lecturerAttendanceClosed) {
      return;
    }

    this.otherExpensesHelperFG.setValue({
      value: this.FG.controls.detailsFG.controls.otherExpenses.controls.value.value,
      currency: this.FG.controls.detailsFG.controls.otherExpenses.controls.currency.value
    });
    if (this.FG.controls.detailsFG.controls.otherExpenses.controls.currency.disabled) {
      this.otherExpensesHelperFG.controls.currency.disable();
    } else {
      this.otherExpensesHelperFG.controls.currency.enable();
    }

    const res = this.modalService.openConfirmModal({
      c: ConfirmModalComponent,
      text: '',
      title: 'admin.session-lecturer-attandance-input-group.other-expenses-modal.title',
      template: this.otherExpensesPriceTemplate,
    });

    res.pipe(
      take(1)
    ).subscribe({
      next: (res) => {
        if (res) {
          this.FG.controls.detailsFG.markAsDirty(); // has to be before patchValue
          this.FG.controls.detailsFG.controls.otherExpenses.patchValue({
            value: this.otherExpensesHelperFG.controls.value.value,
            currency: this.otherExpensesHelperFG.controls.currency.value
          }, { emitEvent: true });
        }
      }
    })
  }

  private createSelectItems(lecturer: ILecturer, defaultLect: boolean = false): SelectItem {
    return {
      value: lecturer.id,
      label: `${this.userNameOrEmailPipe.transform(lecturer.user?.orgUsers ?? [], this.selectedOrg.getCurrentValue(), false)}${defaultLect ? ` (${this.translate.instant('admin.session-lecturer-attendance-modal.default-lecturer')})` : ''}`,
    };
  }

  getBaseReward(): IPrice | null {
    if (!this.data) return null;
    const hourRate = this.data.userSessionLecturer?.hourRate;
    if (!hourRate) return null;

    const session = this.data.session;
    const startAt = new Date(session.startAt).localDateToUTC();
    const endAt = new Date(session.endAt).localDateToUTC();
    const duration = endAt.minutesDelta(startAt) / session.hourDuration;

    if (this.FG.controls.regularFG?.controls.state.value === SessionLecturerState.PRESENT) {
      return { value: +((hourRate.value || 0) * duration).toFixed(1), currency: hourRate.currency };
    } else {
      return { value: 0, currency: hourRate.currency };
    }
  }
  getTotalReward(): IPrice | null {
    const baseReward = this.getBaseReward();
    if (!baseReward) return null;

    const otherExpenses = this.FG.controls.detailsFG.controls.otherExpenses;
    if (!otherExpenses) return baseReward;
    const otherExpensesValue = otherExpenses.controls?.value.value ?? 0;

    return { value: (baseReward.value || 0) + otherExpensesValue, currency: baseReward.currency };
  }

  get lecturingWithFC() {
    if (this.type === SessionLecturerType.REGULAR) {
      return this.FG.controls.regularFG?.controls.lecturingWith;
    } else {
      return this.FG.controls.substitutionFG?.controls.lecturingWith;
    }
  }



  get otherExpensesPriceFG() {
    return this.FG.controls.detailsFG.controls.otherExpenses;
  }


  ngOnDestroy(): void {

    for (let s of this.subs) {
      s.unsubscribe();
    }
  }
}
